import {useEffect} from 'react'
import {buildItem, pushEcommerceEvent} from './ga4DataLayer'

const PROMOTION_SELECTOR = '[data-promotion-id]'
const ITEM_SELECTOR = '[data-item-id], [data-item-name]'
const RESCAN_DELAY = 500

const getPromotionFields = (element) => {
    const {promotionId, promotionName, creativeName, creativeSlot} = element.dataset
    return {
        creative_name: creativeName || undefined,
        creative_slot: creativeSlot || undefined,
        promotion_id: promotionId || undefined,
        promotion_name: promotionName || undefined
    }
}

const getPromotionItems = (element, promotion) => {
    const itemElements = element.matches(ITEM_SELECTOR)
        ? [element]
        : Array.from(element.querySelectorAll(ITEM_SELECTOR))

    return itemElements.map((itemElement, index) =>
        buildItem({
            itemId: itemElement.dataset.itemId,
            itemName: itemElement.dataset.itemName,
            index: Number(itemElement.dataset.index ?? index),
            brand: itemElement.dataset.itemBrand,
            categoryNames: itemElement.dataset.itemCategory,
            variant: itemElement.dataset.itemVariant,
            price: itemElement.dataset.price,
            quantity: itemElement.dataset.quantity ?? 1,
            creativeName: promotion.creative_name,
            creativeSlot: promotion.creative_slot,
            promotionId: promotion.promotion_id,
            promotionName: promotion.promotion_name
        })
    )
}

const buildPromotionEcommerce = (element) => {
    const promotion = getPromotionFields(element)
    if (promotion.promotion_id === undefined) {
        return null
    }
    return {...promotion, items: getPromotionItems(element, promotion)}
}

const usePromotionTracking = () => {
    useEffect(() => {
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return
        const viewedElements = new WeakSet()
        let rescanTimeout = null

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || viewedElements.has(entry.target)) return
                    const ecommerce = buildPromotionEcommerce(entry.target)
                    if (!ecommerce) return
                    viewedElements.add(entry.target)
                    observer.unobserve(entry.target)
                    pushEcommerceEvent('view_promotion', ecommerce)
                })
            },
            {threshold: 0.5}
        )

        const observePromotions = () => {
            document.querySelectorAll(PROMOTION_SELECTOR).forEach((element) => {
                if (!viewedElements.has(element)) observer.observe(element)
            })
        }

        const handleClick = (event) => {
            const element = event.target?.closest?.(PROMOTION_SELECTOR)
            if (!element) return
            const ecommerce = buildPromotionEcommerce(element)
            if (!ecommerce) return
            pushEcommerceEvent('select_promotion', ecommerce)
        }

        const mutationObserver =
            typeof MutationObserver !== 'undefined'
                ? new MutationObserver(() => {
                      clearTimeout(rescanTimeout)
                      rescanTimeout = setTimeout(observePromotions, RESCAN_DELAY)
                  })
                : null

        observePromotions()
        mutationObserver?.observe(document.body, {childList: true, subtree: true})
        document.addEventListener('click', handleClick, true)

        return () => {
            clearTimeout(rescanTimeout)
            observer.disconnect()
            mutationObserver?.disconnect()
            document.removeEventListener('click', handleClick, true)
        }
    }, [])
}

export default usePromotionTracking
